<script setup>
import { computed } from '#imports';
import { hexToRgb } from '~/helpers/hex-to-rgb';

const props = defineProps({
  author: {
    type: Object,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: undefined,
  },
});

const assetId = computed(() => props.author.image.asset._ref);
const markColor = computed(() => {
  const color = props.color ? hexToRgb(props.color) : '251, 255, 53';

  return { '--mark-color': color };
});
</script>

<template>
  <div class="opinion-author content-container" :style="markColor">
    <NuxtLink
      :to="{
        name: 'members-slug',
        params: { slug: author.slug.current },
      }"
      class="opinion-author__content-wrapper"
    >
      <div class="opinion-author__avatar">
        <div class="opinion-author__avatar-backdrop" />
        <SanityImage
          class="opinion-author__avatar-image"
          :asset-id="assetId"
          :h="150"
          :w="160"
          fit="crop"
          dpr="3"
          auto="format"
        />
      </div>
      <div class="opinion-author__info">
        <span class="opinion-author__name">
          {{ author.name }}
        </span>
        <span class="opinion-author__description">
          {{ description }}
        </span>
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="postcss" scoped>
.opinion-author {
  --mark-color: 251, 255, 53;
  --path: 'M160 70C160 114.183 134.183 150 90 150C45.8172 150 0 114.183 0 70C0 25.8172 55.8172 0 100 0C144.183 0 160 25.8172 160 70Z';
  --backdrop-path: 'M164 71.8667C164 117.228 137.537 154 92.25 154C46.9626 154 0 117.228 0 71.8667C0 26.5057 57.2126 0 102.5 0C147.787 0 164 26.5057 164 71.8667Z';

  display: flex;
  padding-top: 40px;
  margin-bottom: -16px;

  &__avatar {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 154px;
    width: 164px;
  }

  &__avatar-image {
    height: 150px;
    width: 160px;
    object-fit: cover;
    object-position: center;
    clip-path: path(var(--path));
  }

  &__avatar-backdrop {
    position: absolute;
    height: 154px;
    width: 164px;
    background-color: rgba(var(--mark-color));
    clip-path: path(var(--backdrop-path));
    transform: scale(100%);
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    padding-bottom: 20px;
    transition: var(--default-transition) opacity;
  }

  &__name {
    font-family: var(--title-font-family);
    font-size: 32px;
    line-height: 46px;
  }

  &__description {
    font-size: 24px;
    line-height: 28px;
    color: rgba(var(--black-color--rgb), 40%);
  }

  &__content-wrapper {
    display: flex;
    gap: 12px;
    z-index: 2;

    &:hover {
      .opinion-author__info {
        opacity: 0.6;
      }
    }
  }

  @media (--small-vp) {
    --path: 'M86 37.3333C86 60.8975 72.1232 80 48.375 80C24.6268 80 0 60.8975 0 37.3333C0 13.7692 30.0018 0 53.75 0C77.4982 0 86 13.7692 86 37.3333Z';
    --backdrop-path: 'M90 39.2C90 63.9424 75.4778 84 50.625 84C25.7722 84 0 63.9424 0 39.2C0 14.4576 31.3972 0 56.25 0C81.1028 0 90 14.4576 90 39.2Z';

    padding-top: 20px;
    margin-bottom: -12px;

    &__avatar {
      height: 84px;
      width: 90px;
    }

    &__avatar-image {
      height: 80px;
      width: 86px;
    }

    &__avatar-backdrop {
      height: 84px;
      width: 90px;
    }

    &__name {
      font-size: 20px;
      line-height: 24px;
    }

    &__description {
      font-size: 16px;
      font-weight: 500;
      line-height: 18px;
    }
  }
}
</style>
